import { useCallback, useMemo, useRef } from 'react'

import { RETIREMENT_AGE_MAX, RETIREMENT_AGE_MIN } from '#profile/schemas'

import { getMoneyStep } from './moneyStep'
import { expenseMultiplierFor, minExpenseRatio } from './spendLessLever'
import type { QuickAdjustments } from './types'
import {
  type UseAdjustmentsState,
  useAdjustmentsState as useAdjustmentsStateDefault,
} from './useAdjustmentsState'

export const MONEY_FLOOR = 0
export const MONEY_CEILING = 10_000_000

// Direction-aware snap so an off-grid current value never moves against the
// user's intent: an "increase" step snaps upward (ceil), a "decrease" step
// snaps downward (floor). Default `nearest` keeps historical behavior for
// initial reads / display and for the on-grid path where direction is
// irrelevant (the new value is already grid-aligned before this fires).
const snapMoney = (
  value: number,
  direction: 1 | -1 | 'nearest' = 'nearest'
): number => {
  const step = getMoneyStep(value)
  const round =
    direction === 1 ? Math.ceil : direction === -1 ? Math.floor : Math.round
  return round(value / step) * step
}

export type ChartBaselines = {
  retirementAge: number
  income: number
  /** Every expense the plan projects — the figure the spend-less lever shows. */
  expenses: number
  /** The part of it the lever may not drag below. */
  nonAdjustableExpenses: number
}

export type AdjustmentSlotType = 'retirementAge' | 'income' | 'expenses'

type BaseSlot = {
  baseline: number
  isModified: boolean
  reset: () => void
  betterWhen: 'lower' | 'higher'
}

export type AgeAdjustmentSlot = BaseSlot & {
  type: 'retirementAge'
  current: number
  min: number
  max: number
  setValue: (next: number) => void
  /**
   * Apply a signed year delta with clamp baked in. Returns `false` if the
   * request would have pushed past `min`/`max` (no movement) — useful for
   * `useHoldAcceleration` to stop scheduling at a boundary.
   */
  step: (delta: number) => boolean
}

export type MoneyAdjustmentSlot = BaseSlot & {
  // Purely a label for now — nothing switches on `type` at runtime. Kept as
  // a discriminator so consumers can inspect it in future without another
  // schema change. `'tuning'` covers quest tuning overrides (ZL-948).
  type: 'income' | 'expenses' | 'tuning'
  ratio: number
  setRatio: (next: number) => void
  /**
   * Apply a signed delta in money-space (not ratio). Clamps at 0 below and
   * at 10× baseline above (matches `useMoneyAdjustment`'s ceiling pattern).
   * Returns `false` when the delta would have pushed past a boundary.
   */
  step: (deltaMoney: number) => boolean
  /**
   * The range this slot may move through, when it is narrower than the global
   * money range. Quest tunings set it from the record's own baseline — the global
   * range is calibrated for What-If's aggregates and would give a $1.2k record a
   * $10M track. Omitted means the global range, which is what What-If wants.
   */
  min?: number
  max?: number
}

export type AdjustmentSlot = AgeAdjustmentSlot | MoneyAdjustmentSlot

export type AdjustmentSlots = {
  retirementAge: AgeAdjustmentSlot
  income: MoneyAdjustmentSlot | null
  expenses: MoneyAdjustmentSlot | null
}

export type UseAdjustmentsResult = {
  slots: AdjustmentSlots | null
  adjustments: QuickAdjustments
  hasAdjustments: boolean
  resetAll: () => void
}

const useAdjustments = (
  baselines: ChartBaselines | null,
  useState: UseAdjustmentsState = useAdjustmentsStateDefault,
  minRetirementAge?: number
): UseAdjustmentsResult => {
  const {
    retirementAge,
    setRetirementAge,
    incomeRatio,
    setIncomeRatio,
    expenseRatio,
    setExpenseRatio,
  } = useState()

  // Kept in sync with state so step functions can accumulate correctly when
  // called multiple times before React has a chance to re-render (e.g. fast swipe).
  const retirementAgeRef = useRef(retirementAge)
  const incomeRatioRef = useRef(incomeRatio)
  const expenseRatioRef = useRef(expenseRatio)
  retirementAgeRef.current = retirementAge
  incomeRatioRef.current = incomeRatio

  /*
   * Clamped where it is read, not only where it is stepped. The floor moves —
   * adding a loan raises it — so a ratio held from before is still a number the
   * lever can no longer reach, and the stepper's own clamp never sees it. Left
   * unclamped, the slot would show a figure below the floor while the
   * projection delivered the floor.
   */
  const expenseFloorRatio = baselines
    ? minExpenseRatio(baselines.expenses, baselines.nonAdjustableExpenses)
    : 0
  const clampedExpenseRatio = Math.max(expenseRatio, expenseFloorRatio)
  expenseRatioRef.current = clampedExpenseRatio

  // Reset callbacks must sync the refs alongside the state setters. Refs
  // otherwise stay at the pre-reset value until React commits the reset,
  // and a step() call in the same event loop would read stale ref data
  // (reset + swipe pressed in quick succession).
  const resetRetirementAge = useCallback(() => {
    retirementAgeRef.current = null
    setRetirementAge(null)
  }, [setRetirementAge])
  const resetIncomeRatio = useCallback(() => {
    incomeRatioRef.current = 0
    setIncomeRatio(0)
  }, [setIncomeRatio])
  const resetExpenseRatio = useCallback(() => {
    expenseRatioRef.current = 0
    setExpenseRatio(0)
  }, [setExpenseRatio])

  const slots = useMemo<AdjustmentSlots | null>(() => {
    if (!baselines) return null

    const currentAge = retirementAge ?? baselines.retirementAge
    const ageFloor = Math.min(
      RETIREMENT_AGE_MAX,
      Math.max(RETIREMENT_AGE_MIN, minRetirementAge ?? RETIREMENT_AGE_MIN)
    )
    const stepAge = (delta: number): boolean => {
      const cur = retirementAgeRef.current ?? baselines.retirementAge
      // Retired user: decrement at the floor resets to baseline instead of blocking.
      if (delta < 0 && cur <= ageFloor && baselines.retirementAge < ageFloor) {
        if (cur > baselines.retirementAge) setRetirementAge(null)
        return false
      }
      const next = Math.min(RETIREMENT_AGE_MAX, Math.max(ageFloor, cur + delta))
      if (next === cur) return false
      retirementAgeRef.current = next
      setRetirementAge(next)
      return true
    }

    const retirementAgeSlot: AgeAdjustmentSlot = {
      type: 'retirementAge',
      baseline: baselines.retirementAge,
      current: currentAge,
      min: ageFloor,
      max: RETIREMENT_AGE_MAX,
      betterWhen: 'lower',
      isModified:
        retirementAge !== null && retirementAge !== baselines.retirementAge,
      setValue: setRetirementAge,
      step: stepAge,
      reset: resetRetirementAge,
    }

    const stepMoney =
      (
        baseline: number,
        ratioRef: { current: number },
        setRatio: (r: number) => void,
        floor: number = MONEY_FLOOR
      ) =>
      (deltaMoney: number): boolean => {
        const currentMoney = Math.round(baseline * (1 + ratioRef.current))
        const nearest = snapMoney(currentMoney)
        // If the current value is off-grid, the first step snaps in the user's
        // requested direction (ceil for +delta, floor for −delta) so an
        // "increase" can never move the value down and vice versa. On-grid
        // values apply the delta directly then re-snap.
        const direction: 1 | -1 = deltaMoney > 0 ? 1 : -1
        const stepped =
          nearest !== currentMoney
            ? snapMoney(currentMoney, direction)
            : snapMoney(currentMoney + deltaMoney)
        // Clamped after snapping, not before: the snap itself can cross the
        // floor, and a floor that is not on the grid — which an engine-charged
        // total rarely is — would otherwise be stepped straight past.
        const next = Math.min(MONEY_CEILING, Math.max(floor, stepped))
        if (next === currentMoney) return false
        ratioRef.current = (next - baseline) / baseline
        setRatio(ratioRef.current)
        return true
      }

    const incomeSlot: MoneyAdjustmentSlot | null =
      baselines.income > 0
        ? {
            type: 'income',
            baseline: baselines.income,
            ratio: incomeRatio,
            betterWhen: 'higher',
            isModified: incomeRatio !== 0,
            setRatio: setIncomeRatio,
            step: stepMoney(baselines.income, incomeRatioRef, setIncomeRatio),
            reset: resetIncomeRatio,
          }
        : null

    const expensesSlot: MoneyAdjustmentSlot | null =
      baselines.expenses > 0
        ? {
            type: 'expenses',
            baseline: baselines.expenses,
            ratio: clampedExpenseRatio,
            betterWhen: 'lower',
            isModified: clampedExpenseRatio !== 0,
            setRatio: setExpenseRatio,
            // What the lever may not drag below: the engine's own charges stay
            // whatever the user does.
            min: baselines.nonAdjustableExpenses,
            step: stepMoney(
              baselines.expenses,
              expenseRatioRef,
              setExpenseRatio,
              baselines.nonAdjustableExpenses
            ),
            reset: resetExpenseRatio,
          }
        : null

    return {
      retirementAge: retirementAgeSlot,
      income: incomeSlot,
      expenses: expensesSlot,
    }
  }, [
    baselines,
    minRetirementAge,
    retirementAge,
    incomeRatio,
    clampedExpenseRatio,
    setRetirementAge,
    setIncomeRatio,
    setExpenseRatio,
    resetRetirementAge,
    resetIncomeRatio,
    resetExpenseRatio,
  ])

  const adjustments = useMemo<QuickAdjustments>(() => {
    if (!baselines) return {}
    const out: QuickAdjustments = {}
    if (retirementAge !== null && retirementAge !== baselines.retirementAge) {
      out.retirementAge = retirementAge
    }
    if (baselines.income > 0 && incomeRatio !== 0)
      out.incomeMultiplier = 1 + incomeRatio
    if (baselines.expenses > 0 && clampedExpenseRatio !== 0) {
      out.expenseMultiplier = expenseMultiplierFor(
        baselines.expenses * (1 + clampedExpenseRatio),
        baselines.expenses,
        baselines.nonAdjustableExpenses
      )
    }
    return out
  }, [baselines, retirementAge, incomeRatio, clampedExpenseRatio])

  const hasAdjustments = Object.keys(adjustments).length > 0

  const resetAll = useCallback(() => {
    retirementAgeRef.current = null
    incomeRatioRef.current = 0
    expenseRatioRef.current = 0
    setRetirementAge(null)
    setIncomeRatio(0)
    setExpenseRatio(0)
  }, [setRetirementAge, setIncomeRatio, setExpenseRatio])

  return { slots, adjustments, hasAdjustments, resetAll }
}

export default useAdjustments
